import React, { useEffect, useState } from 'react';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import Button from '@material-ui/core/Button';
import Grid from '@material-ui/core/Grid';
import TextField from '@material-ui/core/TextField';
import utils from './utils';
import 'src/components/css/pagamento.css';

/**
 * @typedef {Object} PagamentoSplit
 * @property {string} nomeResponsavel
 * @property {number|string} quantidadeCotas - Quando "Convite mesa" é chamado de Cadeiras
 * @property {string} observacao
 * @property {number} valor
 */

export default function DialogSplitPagamento(props) {
  const { open, onClose, onConcluir, valorProduto, inicial } = props;

  const qntDivisoes = 10;
  const valorSplit = (Number(valorProduto) || 0) / qntDivisoes;
  const cardVazio = { nomeResponsavel: '', quantidadeCotas: '', observacao: '', valor: 0 };

  /** @type {[PagamentoSplit[], React.Dispatch<React.SetStateAction<PagamentoSplit[]>>]} */
  const [cardsPagamentoSplit, setCardsPagamentoSplit] = useState([
    { nomeResponsavel: '', quantidadeCotas: '', observacao: '', valor: 0 },
  ]);

  // Carrega os valores iniciais, ou inicia com um card vazio
  useEffect(() => {
    if (open) {
      setCardsPagamentoSplit(inicial && inicial.length > 0 ? inicial.map((c) => ({ ...c })) : [{ ...cardVazio }]);
    }
  }, [open, inicial]);

  const qntCard = (card) => Math.max(0, parseInt(card.quantidadeCotas, 10) || 0);

  const qntAtribuida = cardsPagamentoSplit.reduce((total, card) => total + qntCard(card), 0);
  const valorAtribuido = qntAtribuida * valorSplit;
  const excedeu = qntAtribuida > qntDivisoes;
  const limiteCards = cardsPagamentoSplit.length >= qntDivisoes;

  let status = 'completa';

  if (qntAtribuida < qntDivisoes) {
    status = 'incompleta';
  } else if (excedeu) {
    status = 'excesso';
  }

  const nomeInvalido = (card) => !(card.nomeResponsavel || '').trim();
  const cadeirasInvalidas = (card) => qntCard(card) <= 0;
  const camposPreenchidos = cardsPagamentoSplit.every((card) => !nomeInvalido(card) && !cadeirasInvalidas(card));
  const podeConfirmar = status === 'completa' && camposPreenchidos;

  const mensagens = {
    incompleta: utils.lng('split_incompleto', 'Distribuição incompleta - Distribua todas as cadeiras para continuar'),
    excesso: utils.lng('split_excesso', 'Distribuição incorreta - Atribuido cadeiras em excesso'),
    completa: utils.lng('split_completo', 'Distribuição completa - Pronto para continuar'),
    obrigatorios: utils.lng(
      'split_campos_obrigatorios',
      'Preencha o nome e as cadeiras de todos os pagamentos para continuar',
    ),
  };
  const statusMensagem = status === 'completa' && !camposPreenchidos ? 'obrigatorios' : status;

  const alterarCard = (index, field, value) => {
    setCardsPagamentoSplit((prev) => prev.map((card, i) => (i === index ? { ...card, [field]: value } : card)));
  };

  // Só marca o erro depois que o usuário sai do campo
  const tocarCampo = (index, field) => {
    setCardsPagamentoSplit((prev) =>
      prev.map((card, i) => (i === index ? { ...card, tocados: { ...card.tocados, [field]: true } } : card)),
    );
  };

  const campoComErro = (card, field, invalido) => Boolean(card.tocados && card.tocados[field]) && invalido(card);

  const adicionarCard = () => {
    if (limiteCards) return;

    setCardsPagamentoSplit((prev) => [...prev, { ...cardVazio }]);
  };

  const removerCard = (index) => {
    setCardsPagamentoSplit((prev) => prev.filter((_, i) => i !== index));
  };

  const concluir = () => {
    if (!podeConfirmar) return;

    onConcluir(
      cardsPagamentoSplit.map((card) => ({
        nomeResponsavel: card.nomeResponsavel,
        observacao: card.observacao,
        quantidadeCotas: qntCard(card),
        valor: Math.round(valorSplit * qntCard(card) * 100) / 100,
      })),
    );
  };

  const renderCadeiras = () => {
    const cadeiras = [];

    for (let i = 0; i < qntDivisoes; i++) {
      let className = 'split-cadeira';

      if (excedeu) {
        className += ' split-cadeira-excesso';
      } else if (i < qntAtribuida) {
        className += ' split-cadeira-atribuida';
      }
      cadeiras.push(<div key={'cadeira-' + i} className={className} />);
    }
    return cadeiras;
  };

  return (
    <Dialog open={Boolean(open)} onClose={onClose} maxWidth="md">
      <DialogTitle>{utils.lng('divisao_pagamento', 'Divisão do pagamento')}</DialogTitle>
      <DialogContent>
        <div className="split-card">
          <div className="split-linha">
            <span className="split-bold">
              <span className="split-qntAtribuida"> {qntAtribuida} </span> / {qntDivisoes}{' '}
              {utils.lng('cadeiras_atribuidas', 'cadeiras atribuídas')}
            </span>
            <span>
              {utils.lng('restam', 'Restam')} {Math.max(0, qntDivisoes - qntAtribuida)}
            </span>
          </div>
          <div className="split-cadeiras">{renderCadeiras()}</div>
        </div>

        {cardsPagamentoSplit.map((card, index) => (
          <div key={'pagamento-split-' + index} className="split-card">
            <div className="split-linha">
              <span className="split-bold">
                {utils.lng('pagamento', 'Pagamento')} {index + 1}
              </span>
              {index > 0 && (
                <Button size="small" className="split-remover" onClick={() => removerCard(index)}>
                  {utils.lng('remover', 'Remover')}
                </Button>
              )}
            </div>
            <Grid container spacing={1} className="split-fields">
              <Grid item xs={9}>
                <TextField
                  fullWidth
                  variant="outlined"
                  type="text"
                  required
                  error={campoComErro(card, 'nomeResponsavel', nomeInvalido)}
                  label={utils.lng('nome_pessoa', 'Nome da pessoa')}
                  value={card.nomeResponsavel}
                  onChange={(e) => alterarCard(index, 'nomeResponsavel', e.target.value)}
                  onBlur={() => tocarCampo(index, 'nomeResponsavel')}
                />
              </Grid>
              <Grid item xs={3}>
                <TextField
                  fullWidth
                  variant="outlined"
                  type="number"
                  required
                  error={campoComErro(card, 'quantidadeCotas', cadeirasInvalidas)}
                  inputProps={{ min: 0, max: qntDivisoes }}
                  label={utils.lng('cadeiras', 'Cadeiras')}
                  value={card.quantidadeCotas}
                  onChange={(e) => alterarCard(index, 'quantidadeCotas', e.target.value)}
                  onBlur={() => tocarCampo(index, 'quantidadeCotas')}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  variant="outlined"
                  type="text"
                  label={utils.lng('observacao', 'Observação')}
                  value={card.observacao}
                  onChange={(e) => alterarCard(index, 'observacao', e.target.value)}
                />
              </Grid>
            </Grid>
            <div className="split-divisor" />
            <div className="split-linha">
              <span>{utils.lng('valor_pagamento', 'Valor do pagamento')}</span>
              <span className="split-valor">{utils.money(valorSplit * qntCard(card))}</span>
            </div>
          </div>
        ))}

        <Button
          fullWidth
          variant={limiteCards ? 'outlined' : 'contained'}
          color="primary"
          className={'split-adicionar' + (limiteCards ? ' split-adicionar-desabilitado' : '')}
          disabled={limiteCards}
          onClick={adicionarCard}
          startIcon={<i className="fa fa-plus"></i>}
        >
          {utils.lng('adicionar_pagamento', 'Adicionar pagamento')}
        </Button>

        <div className={'split-resumo split-resumo-' + status}>
          {cardsPagamentoSplit.length}{' '}
          {cardsPagamentoSplit.length === 1
            ? utils.lng('pagamento', 'pagamento').toLowerCase()
            : utils.lng('pagamentos', 'pagamentos').toLowerCase()}{' '}
          • {qntAtribuida}{' '}
          {(qntAtribuida === 1 ? utils.lng('cadeira', 'cadeira') : utils.lng('cadeiras', 'cadeiras')).toLowerCase()} •{' '}
          {utils.money(valorAtribuido)}
        </div>
        <p className={'split-mensagem split-mensagem-' + (statusMensagem === 'obrigatorios' ? 'incompleta' : status)}>
          {mensagens[statusMensagem]}
        </p>
      </DialogContent>
      <DialogActions className="split-dialog-actions">
        <Button variant="contained" className="split-btn-fechar" onClick={onClose}>
          {utils.lng('fechar', 'Fechar')}
        </Button>
        <Button variant="contained" className="split-btn-confirmar" disabled={!podeConfirmar} onClick={concluir}>
          {utils.lng('confirmar', 'Confirmar')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
